
import { SLOT_MS, type BookingRange } from '@shared/booking';
import { instantFromZonedParts, zonedPartsOf } from '@shared/tz';

export const WEEK_DAYS = 7;

export type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const WEEK_STARTS_ON: WeekDay = 1;

export interface SlotRange {
  readonly start: Date;
  readonly end: Date;
}

export function startOfDay(instant: Date, zone: string): Date {
  const { year, month, day } = zonedPartsOf(instant, zone);
  return instantFromZonedParts({ year, month, day, hour: 0, minute: 0 }, zone);
}

function startOfNextDay(instant: Date, zone: string): Date {
  const { year, month, day } = zonedPartsOf(instant, zone);
  return instantFromZonedParts({ year, month, day: day + 1, hour: 0, minute: 0 }, zone);
}

export function startOfWeek(
  instant: Date,
  zone: string,
  weekStartsOn: WeekDay = WEEK_STARTS_ON,
): Date {
  const dayStart = startOfDay(instant, zone);
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(
    dayStart,
  );
  const idx = WEEKDAY_INDEX[weekday] ?? 0;
  const back = (idx - weekStartsOn + WEEK_DAYS) % WEEK_DAYS;
  if (back === 0) return dayStart;
  const { year, month, day } = zonedPartsOf(dayStart, zone);
  return instantFromZonedParts({ year, month, day: day - back, hour: 0, minute: 0 }, zone);
}

const WEEKDAY_INDEX: Record<string, WeekDay> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function addWeeks(weekStart: Date, zone: string, delta: number): Date {
  const { year, month, day } = zonedPartsOf(weekStart, zone);
  return instantFromZonedParts(
    { year, month, day: day + delta * WEEK_DAYS, hour: 0, minute: 0 },
    zone,
  );
}

export function addDays(dayStart: Date, zone: string, delta: number): Date {
  const { year, month, day } = zonedPartsOf(dayStart, zone);
  return instantFromZonedParts({ year, month, day: day + delta, hour: 0, minute: 0 }, zone);
}

export function weekDayStarts(weekStart: Date, zone: string): Date[] {
  const { year, month, day } = zonedPartsOf(weekStart, zone);
  const days: Date[] = [];
  for (let i = 0; i < WEEK_DAYS; i++) {
    days.push(instantFromZonedParts({ year, month, day: day + i, hour: 0, minute: 0 }, zone));
  }
  return days;
}

export function viewerDayKey(instant: Date, zone: string): string {
  const { year, month, day } = zonedPartsOf(instant, zone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export interface DaySlotOptions {
  readonly startHour?: number;
  readonly endHour?: number;
}

export function daySlots(dayStart: Date, zone: string, options: DaySlotOptions = {}): Date[] {
  const { year, month, day } = zonedPartsOf(dayStart, zone);
  const from =
    options.startHour && options.startHour > 0
      ? instantFromZonedParts({ year, month, day, hour: options.startHour, minute: 0 }, zone)
      : dayStart;
  const until =
    options.endHour === undefined
      ? startOfNextDay(dayStart, zone)
      : instantFromZonedParts({ year, month, day, hour: options.endHour, minute: 0 }, zone);

  const slots: Date[] = [];
  for (let t = from.getTime(); t < until.getTime(); t += SLOT_MS) {
    slots.push(new Date(t));
  }
  return slots;
}

export interface WeekGridDay {
  readonly dayStart: Date;
  readonly slots: Date[];
}

export interface WeekGrid {
  readonly weekStart: Date;
  readonly days: WeekGridDay[];
}

export function buildWeekGrid(weekStart: Date, zone: string, options: DaySlotOptions = {}): WeekGrid {
  const days = weekDayStarts(weekStart, zone).map((dayStart) => ({
    dayStart,
    slots: daySlots(dayStart, zone, options),
  }));
  return { weekStart, days };
}

export function weekRangeLabel(weekStart: Date, zone: string): string {
  return multiDayRangeLabel(weekStart, zone, WEEK_DAYS);
}

export function multiDayRangeLabel(firstDayStart: Date, zone: string, dayCount: number): string {
  const end = addDays(firstDayStart, zone, dayCount - 1);
  const s = zonedPartsOf(firstDayStart, zone);
  const e = zonedPartsOf(end, zone);
  const sameMonth = s.year === e.year && s.month === e.month;
  const startText = `${MONTHS[s.month - 1]} ${s.day}`;
  const endText = sameMonth ? `${e.day}` : `${MONTHS[e.month - 1]} ${e.day}`;
  return `${startText} – ${endText}`;
}

export function dayRangeLabel(dayStart: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: zone || undefined,
  }).format(dayStart);
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function slotRange(slotStart: Date): SlotRange {
  return { start: slotStart, end: new Date(slotStart.getTime() + SLOT_MS) };
}

export function rangeCoversSlot(range: BookingRange, slotStart: Date): boolean {
  const cell = slotRange(slotStart);
  return range.start.getTime() < cell.end.getTime() && range.end.getTime() > cell.start.getTime();
}

function anyCovers(ranges: readonly BookingRange[], slotStart: Date): boolean {
  return ranges.some((r) => rangeCoversSlot(r, slotStart));
}

export type SlotStatus = 'free' | 'booked' | 'unavailable';

export function slotStatus(
  slotStart: Date,
  bookings: readonly BookingRange[],
  unavailability: readonly BookingRange[],
): SlotStatus {
  if (anyCovers(unavailability, slotStart)) return 'unavailable';
  if (anyCovers(bookings, slotStart)) return 'booked';
  return 'free';
}

function floorToSlot(instant: Date): Date {
  return new Date(Math.floor(instant.getTime() / SLOT_MS) * SLOT_MS);
}

export function normalizeSelection(anchor: Date, focus: Date): BookingRange {
  const a = floorToSlot(anchor).getTime();
  const b = floorToSlot(focus).getTime();
  const startMs = Math.min(a, b);
  const lastSlotMs = Math.max(a, b);
  return { start: new Date(startMs), end: new Date(lastSlotMs + SLOT_MS) };
}

export function slotCount(range: BookingRange): number {
  return Math.round((range.end.getTime() - range.start.getTime()) / SLOT_MS);
}
